import { apiClient, ApiResponse } from './client'

/**
 * LC 어드민(교사) — 단원평가 특강 API.
 * 백엔드 컨트롤러: LcUnitAssessmentController (/lc/admin/coaching/...). 권한 COACHING.
 * quizId 는 전부 String(Snowflake), quizSetId/anchorLessonId/id 는 일반 PK(number 안전).
 */

export type UaMode = 'PRINT' | 'ONLINE'
export type UaStatus =
  | 'PENDING'
  | 'STARTED'
  | 'SUBMITTED'
  | 'GRADED'
  | 'REVIEW_OPEN'
  | 'COMPLETED'
  | 'CANCELLED'

export interface UaCatalogSet {
  quizSetId: number
  grade: number
  semester: number
  unitNo: number
  unitName: string
  name: string
  memberCount: number
  isActive: boolean
}

export interface UaCatalog {
  grades: number[]
  sets: UaCatalogSet[]
}

export interface UaSummary {
  id: number
  quizSetId: number | null
  setName: string
  mode: UaMode
  status: UaStatus
  anchorLessonId: number
  position: string
  score: number | null
  createdAt: string
  completedAt: string | null
}

export interface UaItemDetail {
  quizId: string
  sortOrder: number
  answer: string | null
  gradedIsCorrect: boolean | null
  corrects?: (boolean | null)[] | null
  videoWatched: boolean
  hasSolutionVideo: boolean
  correctAnswer: unknown[] | null
  questionSnapshot: Record<string, unknown> | null
  explanation: string | null
}

export interface UaDetail {
  id: number
  quizSetId: number | null
  setName: string
  mode: UaMode
  status: UaStatus
  anchorLessonId: number
  position: string
  score: number | null
  items: UaItemDetail[]
}

export interface CreateUaRequest {
  quizSetId: number
  mode: UaMode
  anchorLessonId: number
}

export interface UpdateUaPlacementRequest {
  anchorLessonId: number
}

export const unitAssessmentAdminApi = {
  getCatalog: async (includeInactive = false): Promise<UaCatalog> => {
    const res = await apiClient.get<ApiResponse<UaCatalog>>(
      '/lc/admin/coaching/unit-assessment-catalog',
      { params: { includeInactive } },
    )
    return res.data.data
  },
  list: async (learnerId: number): Promise<UaSummary[]> => {
    const res = await apiClient.get<ApiResponse<UaSummary[]>>(
      `/lc/admin/coaching/learners/${learnerId}/unit-assessments`,
    )
    return res.data.data
  },
  create: async (learnerId: number, body: CreateUaRequest): Promise<UaSummary> => {
    const res = await apiClient.post<ApiResponse<UaSummary>>(
      `/lc/admin/coaching/learners/${learnerId}/unit-assessments`,
      body,
    )
    return res.data.data
  },
  updatePlacement: async (
    learnerId: number,
    id: number,
    body: UpdateUaPlacementRequest,
  ): Promise<UaSummary> => {
    const res = await apiClient.patch<ApiResponse<UaSummary>>(
      `/lc/admin/coaching/learners/${learnerId}/unit-assessments/${id}/placement`,
      body,
    )
    return res.data.data
  },
  // detail 응답 quizId 는 백엔드에서 String 직렬화(QuizInfoView.quizId 포함) → 일반 JSON 안전.
  getDetail: async (learnerId: number, id: number): Promise<UaDetail> => {
    const res = await apiClient.get<ApiResponse<UaDetail>>(
      `/lc/admin/coaching/learners/${learnerId}/unit-assessments/${id}`,
    )
    return res.data.data
  },
  startPrint: async (learnerId: number, id: number): Promise<UaSummary> => {
    const res = await apiClient.post<ApiResponse<UaSummary>>(
      `/lc/admin/coaching/learners/${learnerId}/unit-assessments/${id}/start-print`,
      {},
    )
    return res.data.data
  },
  gradePrintItem: async (
    learnerId: number,
    id: number,
    quizId: string,
    answer?: string,
    overrideIsCorrect?: boolean,
  ): Promise<UaDetail> => {
    const res = await apiClient.post<ApiResponse<UaDetail>>(
      `/lc/admin/coaching/learners/${learnerId}/unit-assessments/${id}/print/grade-item`,
      { quizId, answer, overrideIsCorrect },
    )
    return res.data.data
  },
  complete: async (learnerId: number, id: number): Promise<UaDetail> => {
    const res = await apiClient.post<ApiResponse<UaDetail>>(
      `/lc/admin/coaching/learners/${learnerId}/unit-assessments/${id}/complete`,
      {},
    )
    return res.data.data
  },
  cancel: async (learnerId: number, id: number): Promise<Record<string, string>> => {
    const res = await apiClient.delete<ApiResponse<Record<string, string>>>(
      `/lc/admin/coaching/learners/${learnerId}/unit-assessments/${id}`,
    )
    return res.data.data
  },
  getPrintQuizIds: async (learnerId: number, id: number): Promise<string[]> => {
    const res = await apiClient.get<ApiResponse<string[]>>(
      `/lc/admin/coaching/learners/${learnerId}/unit-assessments/${id}/print-quizzes`,
    )
    return res.data.data
  },
}
